import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ClientResponseError } from 'pocketbase';
import { publicRoutes } from '@config/routes';
import authIllustrationLight from '@assets/images/auth/auth-login-illustration-light.png';
import authIllustrationDark from '@assets/images/auth/auth-login-illustration-dark.png';
import platformBgLight from '@assets/images/auth/bg-shape-image-light.png';
import platformBgDark from '@assets/images/auth/bg-shape-image-dark.png';
import { usePasswordReset } from '@features/auth/hooks';
import './ResetPasswordPage.scss';

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Mật khẩu phải có ít nhất 8 ký tự'),
    confirmPassword: z.string().min(8, 'Vui lòng nhập lại mật khẩu'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { confirmPasswordReset } = usePasswordReset();

  useEffect(() => {
    const tokenFromQuery = searchParams.get('token');
    if (!tokenFromQuery) {
      navigate(publicRoutes.forgotPassword, { replace: true });
      return;
    }
    setToken(tokenFromQuery);
  }, [searchParams, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) return;

    setIsSubmitting(true);

    try {
      await confirmPasswordReset(token, values.password, values.confirmPassword);

      toast.success('Đặt lại mật khẩu thành công, vui lòng đăng nhập lại');
      navigate(publicRoutes.login, { replace: true });
    } catch (error) {
      let message = 'Đặt lại mật khẩu thất bại. Vui lòng thử lại.';

      if (error instanceof ClientResponseError) {
        if (error.status === 400) {
          message = 'Dữ liệu không hợp lệ, vui lòng kiểm tra lại.';
        } else if (error.status >= 500) {
          message = 'Máy chủ gặp sự cố, vui lòng thử lại sau.';
        }
      }

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-cover-page reset-password-page">
      <div className="authentication-wrapper authentication-cover authentication-bg">
        <div className="authentication-inner row g-0">
          <div className="d-none d-lg-flex col-lg-7 p-0">
            <div className="auth-cover-bg d-flex flex-column justify-content-center align-items-center position-relative">
              <img
                src={authIllustrationLight}
                alt="Đặt lại mật khẩu HRM SaaS"
                className="img-fluid my-5 auth-illustration"
                data-app-light-img={authIllustrationLight}
                data-app-dark-img={authIllustrationDark}
              />

              <img
                src={platformBgLight}
                alt="Trang trí nền"
                className="platform-bg"
                data-app-light-img={platformBgLight}
                data-app-dark-img={platformBgDark}
              />
            </div>
          </div>

          <div className="d-flex col-12 col-lg-5 align-items-center p-sm-5 p-4">
            <div className="mx-auto" style={{ maxWidth: 400, width: '100%' }}>
              <div className="app-brand mb-4 d-inline-flex align-items-center gap-2">
                <span className="app-brand-logo">
                  <svg width="32" height="22" viewBox="0 0 32 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.00172773 0V6.85398C0.00172773 6.85398 -0.133178 9.01207 1.98092 10.8388L13.6912 21.9964L19.7809 21.9181L18.8042 9.88248L16.4951 7.17289L9.23799 0H0.00172773Z"
                      fill="#7367F0"
                    />
                    <path
                      opacity="0.06"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.69824 16.4364L12.5199 3.23696L16.5541 7.25596L7.69824 16.4364Z"
                      fill="#161616"
                    />
                    <path
                      opacity="0.06"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.07751 15.9175L13.9419 4.63989L16.5849 7.28475L8.07751 15.9175Z"
                      fill="#161616"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.77295 16.3566L23.6563 0H32V6.88383C32 6.88383 31.8262 9.17836 30.6591 10.4057L19.7824 22H13.6938L7.77295 16.3566Z"
                      fill="#7367F0"
                    />
                  </svg>
                </span>
                <span className="app-brand-text fw-bold fs-4 text-dark">HRM SaaS</span>
              </div>

              <div className="authentication-form">
                <div>
                  <h3 className="mb-1 fw-bold">Đặt lại mật khẩu 🔒</h3>
                  <p className="mb-4">Tạo mật khẩu mới cho tài khoản của bạn để bảo mật tốt hơn.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="mb-3">
                  <div className="mb-3 form-password-toggle">
                    <label className="form-label" htmlFor="password">
                      Mật khẩu mới
                    </label>
                    <div className="input-group input-group-merge">
                      <input
                        type="password"
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        id="password"
                        placeholder="••••••••••••"
                        {...register('password')}
                      />
                      <span className="input-group-text cursor-pointer">
                        <i className="ti ti-eye-off" />
                      </span>
                    </div>
                    {errors.password && <div className="invalid-feedback d-block">{errors.password.message}</div>}
                  </div>

                  <div className="mb-3 form-password-toggle">
                    <label className="form-label" htmlFor="confirm-password">
                      Xác nhận mật khẩu
                    </label>
                    <div className="input-group input-group-merge">
                      <input
                        type="password"
                        className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                        id="confirm-password"
                        placeholder="••••••••••••"
                        {...register('confirmPassword')}
                      />
                      <span className="input-group-text cursor-pointer">
                        <i className="ti ti-eye-off" />
                      </span>
                    </div>
                    {errors.confirmPassword && (
                      <div className="invalid-feedback d-block">{errors.confirmPassword.message}</div>
                    )}
                  </div>

                  <button className="btn btn-primary d-grid w-100 mb-3" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Đang cập nhật...' : 'Đặt lại mật khẩu'}
                  </button>

                  <div className="text-center">
                    <Link to={publicRoutes.login}>
                      <i className="ti ti-chevron-left" />
                      <span className="ms-1">Quay lại đăng nhập</span>
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}